import { chat } from "@/lib/http";

export interface Invitation {
  id: string;
  account_id: string;
  token: string;
  invite_url: string;
  role: string;
  email: string | null;
  status: string;
  expires_at: string | null;
  accepted_by: string | null;
  accepted_at: string | null;
  created_at: string;
}

export interface InvitationWithAccount extends Invitation {
  account_name?: string;
}

export interface ManageMember {
  id: string;
  account_id: string;
  user_id: string;
  role: string;
  status: string;
  joined_at: string | null;
  first_name: string | null;
  last_name: string | null;
  username?: string | null;
  avatar_url: string | null;
  email: string | null;
  phone: string | null;
  user_type?: string;
  bot_id?: string | null;
  is_bot?: boolean;
}

export interface AccountToken {
  id: string;
  name: string;
  token: string;
}

export interface BotUserProjection {
  id: string;
  first_name: string | null;
  last_name: string | null;
  username: string | null;
  is_bot: boolean;
}

export interface AccountBot {
  id: string;
  account_id: string;
  user_id: string;
  username: string;
  display_name: string;
  description: string | null;
  about: string | null;
  avatar_url: string | null;
  status: string;
  privacy_mode: boolean;
  guest_dm_enabled: boolean;
  auto_start_enabled: boolean;
  auto_start_command: string;
  join_groups_enabled: boolean;
  commands_enabled: boolean;
  created_at: string | null;
  updated_at: string | null;
  user: BotUserProjection;
}

export interface AccountBotCreateResponse extends AccountBot {
  token: string;
}

export interface BotCommand {
  command: string;
  description: string;
}

export interface BotWebhookInfo {
  url: string;
  has_custom_certificate: boolean;
  pending_update_count: number;
  last_error_date: number | null;
  last_error_message: string | null;
  max_connections: number;
  allowed_updates: string[];
  secret_token?: string | null;
}

export interface BotMiniAppInfo {
  bot_id: string;
  is_enabled: boolean;
  launch_url: string;
  title: string | null;
  description: string | null;
  icon_url: string | null;
  open_mode: string;
  display_mode: string;
  capabilities: Record<string, boolean>;
  require_write_access: boolean;
  created_at: string | null;
  updated_at: string | null;
}

export function updateAccount(
  accountId: string,
  data: { name?: string; enable_global_address_book?: boolean; avatar_url?: string | null },
) {
  return chat<{ id: string; name: string; slug: string; enable_global_address_book: boolean }>(
    `/v1/chat/accounts/${accountId}/manage/settings`,
    { method: "PATCH", body: JSON.stringify(data) },
  );
}

export function listManageMembers(accountId: string, query?: string) {
  const params = query ? `?q=${encodeURIComponent(query)}` : "";
  return chat<ManageMember[]>(
    `/v1/chat/accounts/${accountId}/manage/members${params}`,
  );
}

export function updateManageMember(
  accountId: string,
  userId: string,
  data: { role?: string; status?: string },
) {
  return chat<ManageMember>(`/v1/chat/accounts/${accountId}/manage/members/${userId}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export function createInvitation(
  accountId: string,
  data: { emails?: string[]; role?: string; expires_days?: number },
) {
  return chat<Invitation[]>(`/v1/chat/accounts/${accountId}/manage/invitations`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function listInvitations(accountId: string) {
  return chat<Invitation[]>(`/v1/chat/accounts/${accountId}/manage/invitations`);
}

export function deleteInvitation(accountId: string, invitationId: string) {
  return chat<void>(`/v1/chat/accounts/${accountId}/manage/invitations/${invitationId}`, {
    method: "DELETE",
  });
}

export function resendInvitation(accountId: string, invitationId: string) {
  return chat<{ ok: boolean }>(`/v1/chat/accounts/${accountId}/manage/invitations/${invitationId}/resend`, {
    method: "POST",
  });
}

export function createAccountToken(accountId: string, data: { name: string }) {
  return chat<AccountToken>(`/v1/chat/accounts/${accountId}/manage/tokens`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function acceptInvitation(token: string) {
  return chat<InvitationWithAccount>(`/v1/chat/invitations/${token}`, {
    method: "PATCH",
  });
}

export function listBots(accountId: string) {
  return chat<AccountBot[]>(`/v1/chat/accounts/${accountId}/bots`);
}

export function createBot(
  accountId: string,
  data: {
    username: string;
    display_name: string;
    description?: string;
    about?: string;
    avatar_url?: string | null;
    privacy_mode?: boolean;
    guest_dm_enabled?: boolean;
    auto_start_enabled?: boolean;
    auto_start_command?: string;
  },
) {
  return chat<AccountBotCreateResponse>(`/v1/chat/accounts/${accountId}/bots`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function updateBot(
  accountId: string,
  botId: string,
  data: {
    username?: string;
    display_name?: string;
    description?: string | null;
    about?: string | null;
    avatar_url?: string | null;
    privacy_mode?: boolean;
    guest_dm_enabled?: boolean;
    auto_start_enabled?: boolean;
    auto_start_command?: string;
    status?: string;
  },
) {
  return chat<AccountBot>(`/v1/chat/accounts/${accountId}/bots/${botId}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export function createBotToken(accountId: string, botId: string, data: { name: string }) {
  return chat<AccountToken>(`/v1/chat/accounts/${accountId}/bots/${botId}/tokens`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function getBotCommands(accountId: string, botId: string) {
  return chat<BotCommand[]>(`/v1/chat/accounts/${accountId}/bots/${botId}/commands`);
}

export function setBotCommands(
  accountId: string,
  botId: string,
  data: { commands: BotCommand[]; scope: { type: string; chat_id?: string | null } },
) {
  return chat<BotCommand[]>(`/v1/chat/accounts/${accountId}/bots/${botId}/commands`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export function getBotWebhook(accountId: string, botId: string) {
  return chat<BotWebhookInfo>(`/v1/chat/accounts/${accountId}/bots/${botId}/webhook`);
}

export function setBotWebhook(
  accountId: string,
  botId: string,
  data: { url: string; secret_token?: string | null; allowed_updates?: string[] | null },
) {
  return chat<boolean>(`/v1/chat/accounts/${accountId}/bots/${botId}/webhook`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export function deleteBotWebhook(accountId: string, botId: string) {
  return chat<boolean>(`/v1/chat/accounts/${accountId}/bots/${botId}/webhook`, {
    method: "DELETE",
  });
}

export function getBotMiniApp(accountId: string, botId: string) {
  return chat<BotMiniAppInfo>(`/v1/chat/accounts/${accountId}/bots/${botId}/mini-app`);
}

export function setBotMiniApp(
  accountId: string,
  botId: string,
  data: {
    launch_url: string;
    title?: string | null;
    description?: string | null;
    icon_url?: string | null;
    open_mode?: string;
    display_mode?: string;
    capabilities?: Record<string, boolean>;
    require_write_access?: boolean;
    is_enabled?: boolean;
  },
) {
  return chat<BotMiniAppInfo>(`/v1/chat/accounts/${accountId}/bots/${botId}/mini-app`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}
